import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, useLongPress } from "@tosize/ui";
import { Slider as SliderPrimitive } from "radix-ui";
import { useId } from "react";
import { FormattedNumber } from "react-intl";

interface SliderProps extends SliderPrimitive.SliderProps {
	onValueDown: () => void;
	onValueUp: () => void;
	shouldTransformMMToCM?: boolean;
}

export function Slider(props: SliderProps) {
	const {
		onValueDown,
		onValueUp,
		onPointerDown,
		shouldTransformMMToCM = false,
		className,
		...rest
	} = props;
	const sliderThumbId = useId();
	const value = rest.value?.at(0) ?? 0;
	const transformedValue = shouldTransformMMToCM ? value / 10 : value;
	const downDisabled = rest.disabled ?? rest.min === value;
	const upDisabled = rest.disabled ?? rest.max === value;
	const longPressDown = useLongPress(!downDisabled ? onValueDown : undefined);
	const longPressUp = useLongPress(!upDisabled ? onValueUp : undefined);

	return (
		<div className={cn("flex flex-col gap-y-1", className)}>
			<div className="flex items-center gap-2">
				<button
					type="button"
					className="flex aspect-square w-[22px] shrink-0 appearance-none items-center justify-center rounded-full border-2 border-white enter:border-primary-medium bg-white text-shades-dark text-sm leading-none focus:outline-none disabled:cursor-not-allowed disabled:border-shades-light disabled:bg-shades-medium-light disabled:text-shades-medium motion-safe:transition-colors"
					disabled={downDisabled}
					onClick={() => onValueDown()}
					{...longPressDown}
				>
					<FontAwesomeIcon icon={faMinus} />
				</button>
				<SliderPrimitive.Root
					className="relative flex h-[22px] w-full touch-none select-none items-center"
					onPointerDown={(e) => {
						if (e.target instanceof HTMLElement && e.target.id !== sliderThumbId) {
							e.preventDefault();
						}

						return onPointerDown?.(e);
					}}
					{...rest}
				>
					<SliderPrimitive.Track className="relative h-[3px] grow rounded-full bg-shades">
						<SliderPrimitive.Range className="absolute h-full rounded-full bg-primary-medium" />
					</SliderPrimitive.Track>

					<SliderPrimitive.Thumb
						id={sliderThumbId}
						className="block cursor-grab whitespace-nowrap rounded-xl border-2 border-shades enter:border-primary-medium bg-white px-2 py-0.5 font-bold text-shades-dark text-sm leading-none focus:outline-none active:cursor-grabbing motion-safe:transition-colors"
					>
						<FormattedNumber value={transformedValue} style="unit" unit="centimeter" />
					</SliderPrimitive.Thumb>
				</SliderPrimitive.Root>

				<button
					type="button"
					className="flex aspect-square w-[22px] shrink-0 appearance-none items-center justify-center rounded-full border-2 border-white enter:border-primary-medium bg-white text-shades-dark text-sm leading-none focus:outline-none disabled:cursor-not-allowed disabled:border-shades-light disabled:bg-shades-medium-light disabled:text-shades-medium motion-safe:transition-colors"
					disabled={upDisabled}
					onClick={() => onValueUp()}
					{...longPressUp}
				>
					<FontAwesomeIcon icon={faPlus} />
				</button>
			</div>

			{rest.min !== undefined && rest.max !== undefined && (
				<div className="flex justify-between text-shades-medium text-xs leading-none">
					<span>
						<FormattedNumber
							value={shouldTransformMMToCM ? rest.min / 10 : rest.min}
							style="unit"
							unit="centimeter"
						/>
					</span>

					<span>
						<FormattedNumber
							value={shouldTransformMMToCM ? rest.max / 10 : rest.max}
							style="unit"
							unit="centimeter"
						/>
					</span>
				</div>
			)}
		</div>
	);
}
